import { Formik } from "formik";
import React from "react";
import * as Yup from "yup";
import CustomInput from "../CustomInput";
import { Button } from "../ui/button";
import { cn, getCustomizationValue, showSuccessToast } from "src/lib/utils";
import { useAuth } from "src/providers/AuthProvider";
import { createSubAccount, editSubAccount } from "src/api/subAccount";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { usePortalContext } from "src/context/PortalContext";
import FormLabel from "../FormLabel";
import ComboSearch from "../ComboSearch";
import { getCustomers } from "src/api/customer";

const validationSchema = Yup.object().shape({
	firstname: Yup.string().required(),
	lastname: Yup.string().required(),
	othername: Yup.string(),
	idNo: Yup.string(),
	customer_id: Yup.string().required(),
	businessUuid: Yup.string().required(),
});

const AddSubAccountWithParent = ({ subAccount }) => {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const closePortal = usePortalContext();

	const initialValues = {
		uuid: subAccount?.uuid ?? undefined,
		firstname: subAccount?.firstname ?? "",
		lastname: subAccount?.lastname ?? "",
		othername: subAccount?.othername ?? "",
		idNo: subAccount?.idNo ?? "",
		customer_id: subAccount?.customer_id ?? "",
		businessUuid: user?.business_uuid,
	};

	const { mutate, isLoading } = useMutation(subAccount ? editSubAccount : createSubAccount, {
		onSuccess: ({ message }) => {
			showSuccessToast(message);
			queryClient.invalidateQueries(["subAccounts"]);
			closePortal();
		},
	});
	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ values, handleChange, handleSubmit, setFieldValue }) => (
				<form onSubmit={handleSubmit} className="tw-grid tw-gap-4 tw-h-fit">
					<CustomInput
						label="First Name"
						name="firstname"
						type="text"
						placeholder="First Name"
						value={values.firstname}
						onChange={handleChange}
					/>
					<CustomInput
						label="Last Name"
						name="lastname"
						type="text"
						placeholder="Last Name"
						value={values.lastname}
						onChange={handleChange}
					/>
					<CustomInput
						label="Other Name (optional)"
						name="othername"
						type="text"
						placeholder="Other Name"
						value={values.othername}
						onChange={handleChange}
					/>
					<CustomInput
						label="Id Number (optional)"
						name="idNo"
						type="text"
						placeholder="Id Number (optional)"
						value={values.idNo}
						onChange={handleChange}
					/>
					<FormLabel name="customer_id">
						<ComboSearch
							value={values.customer_id}
							onChange={(value) => {
								setFieldValue("customer_id", value);
							}}
							placeholder={cn("Search ", getCustomizationValue(user, "customer_label", true), "...")}
							className="tw-justify-between tw-w-full"
							optionRenderer={({ first_name, last_name }) => (
								<div className="flex gap-2">
									<p>
										{first_name} {last_name}
									</p>
								</div>
							)}
							queryKey="customers"
							queryFN={getCustomers}
							mapFN={(option) => ({
								label: `${option.first_name} ${option.last_name}`,
								value: String(option.id),
								info: option,
							})}
						/>
					</FormLabel>
					<Button disabled={isLoading} className="mt-2">
						Add {getCustomizationValue(user, "sub_account_label", true)}
					</Button>
				</form>
			)}
		</Formik>
	);
};

export default AddSubAccountWithParent;
